import { useCallback } from 'react';
import { equals, omit } from 'ramda';
import { useApolloClient, FetchPolicy } from '@apollo/client';
import { logger } from '../../lib/logger';
import { useCountry } from '../../components/CountrySelector';
import { useErrorModal } from '../useErrorModal';
import { useCookies } from '../useCookies';
import { useUser } from '../useUser';
import { GET_CART_QUERY, useCart, useCartVersion } from '../useCart';
import { ADDRESSES_QUERY, useAddressesQuery } from '../useAddress';
import { PAYMENT_METHODS_QUERY, usePaymentMethodsQuery } from '../usePaymentMethods';

// refetches the data and verifies that the selected address and payment are persisted before placing an order
// to make sure the order is placed with the shipping and payment information that is displayed to the user
export const useVerifyShippingAndPayment = () => {
  const client = useApolloClient();
  const {
    cookies: { cartId }
  } = useCookies();
  const { user } = useUser();
  const { cart } = useCart();
  const { addresses } = useAddressesQuery();
  const { data: paymentMethods } = usePaymentMethodsQuery();
  const { country } = useCountry();
  const cartVersion = useCartVersion();
  const { showErrorModal } = useErrorModal();

  const getShippingAndPayment = useCallback(
    async (fetchPolicy: FetchPolicy) => {
      const [cartQuery, addressesQuery, paymentsQuery] = await Promise.all([
        client.query({
          query: GET_CART_QUERY,
          variables: { cartId: cartId || '', cartVersion, countryCode: country.alpha2Code },
          fetchPolicy
        }),
        client.query({ query: ADDRESSES_QUERY, fetchPolicy }),
        client.query({ query: PAYMENT_METHODS_QUERY, fetchPolicy })
      ]);

      const addresses = addressesQuery.data.addresses.data;
      const paymentMethods = paymentsQuery.data.paymentMethods.data;
      const cart = cartQuery.data.userCart.data[0];

      const address = addresses.find(address => address.id === String(cart.attributes.addressId));
      const paymentMethod = paymentMethods.find(
        paymentMethod => paymentMethod.id === String(cart.attributes.paymentMethodId)
      );

      return {
        address,
        paymentMethod: paymentMethod && {
          ...paymentMethod,
          attributes: omit(['resultCode', 'psd2AuthData', 'isValid'], paymentMethod.attributes)
        }
      };
    },
    [client, cartId, country.alpha2Code, cartVersion]
  );

  const verifyShippingAndPayment = useCallback(async () => {
    const oldShippingAndPayment = await getShippingAndPayment('cache-first');
    const newShippingAndPayment = await getShippingAndPayment('network-only');

    const isValid = equals(oldShippingAndPayment, newShippingAndPayment);

    if (!isValid) {
      showErrorModal(
        'Seems like the shipping and/or payment information has been changed, please double check the data before you proceed.'
      );
      logger.info(
        'CHECKOUT_INFORMATION_OUTDATED',
        'Outdated shipping and/or payment info on checkout.',
        {
          email: user.email,
          guest: !user.isLoggedIn,
          oldShippingAndPayment: JSON.stringify(oldShippingAndPayment),
          newShippingAndPayment: JSON.stringify(newShippingAndPayment),
          cart: JSON.stringify(cart),
          addresses: JSON.stringify(addresses),
          paymentMethods: JSON.stringify(paymentMethods?.paymentMethods.data)
        }
      );
    }

    return isValid;
  }, [user, cart, addresses, paymentMethods, showErrorModal, getShippingAndPayment]);

  return verifyShippingAndPayment;
};
